import React from 'react';
import { useLocation } from 'react-router-dom';
import TalkToDesigner from './TalkToDesigner';
import { OUTREACH_NOTES } from '../../config/whatsapp';

/**
 * The floating "Talk to a designer" pill, on the surfaces where a buyer is
 * still looking rather than committing.
 *
 * An allowlist, not a blocklist. A persistent WhatsApp button that leaks onto
 * the order, payment or messaging routes pulls buyers off the order chat that
 * buyer protection depends on, so new routes stay quiet until someone decides
 * they should have it.
 */

/**
 * Discovery and marketing surfaces. Exact match, or a prefix with `*`.
 *
 * A post (`/archives/<id>`) and a product carry their own WhatsApp button next
 * to their own CTA, seeded with the piece on screen — a floating pill there
 * would be a second, vaguer route to the same conversation.
 */
const SHOW_ON = [
  '/',
  '/archives',
  '/edits',
  '/edits/*',
  '/designers',
  '/p/*',
  '/about',
  '/contact',
  '/help',
  '/help/*',
];

/**
 * Routes that already own the bottom of a phone screen — the edit page pins
 * its own occasion prompt there. The pill would land on top of it, so on
 * mobile it stays off and the page's own CTA keeps the space.
 */
const DESKTOP_ONLY = ['/edits/*'];

const matches = (pathname: string, patterns: string[]) =>
  patterns.some((p) =>
    p.endsWith('/*') ? pathname.startsWith(p.slice(0, -1)) && pathname !== p.slice(0, -2) : pathname === p
  );

const TalkToDesignerFloat: React.FC = () => {
  const { pathname } = useLocation();

  if (!matches(pathname, SHOW_ON)) return null;

  const desktopOnly = matches(pathname, DESKTOP_ONLY);
  const onEdit = pathname.startsWith('/edits/');

  return (
    <TalkToDesigner
      variant="float"
      surface={`float_${pathname === '/' ? 'home' : pathname.split('/')[1]}`}
      note={onEdit ? OUTREACH_NOTES.edit() : OUTREACH_NOTES.general}
      className={desktopOnly ? 'hidden md:inline-flex' : ''}
    />
  );
};

export default TalkToDesignerFloat;
